import React from 'react';
import styled from 'styled-components';
import { system, extractSysProps } from './system';

// Replicates molekule Column:
//   box-sizing: border-box
//   flex: 1 0 auto
//   flex-direction: column
//   padding-left/right: gutter/2 (0 if collapse)
//   width/flex-basis props from xs/sm/md/lg/xl breakpoint props
//
// Column is also used directly with width (px number) and system props
// (mt, pb) in many call sites.
//
// Breakpoint props (xs, sm, md, lg, xl): number 1-12 → percentage of 12-col grid
// xsOffset, smOffset, ... → margin-left offset

export type ColumnProps = {
  gutter?: number;
  collapse?: boolean;
  vertical?: boolean;
  // width as a direct pixel value (from ReadOnlyColumn, FieldColumn, etc.)
  width?: number | string;
  // 12-column breakpoint sizes
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  xsOffset?: number;
  smOffset?: number;
  mdOffset?: number;
  lgOffset?: number;
  xlOffset?: number;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any;
};


// Breakpoints from molekule defaultBreakpoints
const BREAKPOINTS: Record<string, string> = {
  xs: '400px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1500px',
};

const GRID_COLUMNS = 12;

function widthPercent(size: number): string {
  return `${(size / GRID_COLUMNS) * 100}%`;
}

function getColumnWidthCSS(size: number): string {
  if (size / GRID_COLUMNS === 1) {
    return 'max-width: 100%; width: 100%; flex: auto;';
  }
  const pct = widthPercent(size);
  return `display: block; flex-basis: ${pct}; max-width: ${pct};`;
}

function getOffsetCSS(offset: number): string {
  if (offset === 0) return 'margin-left: 0;';
  return `margin-left: ${widthPercent(offset)};`;
}

interface StyledColumnInternalProps {
  $gutter?: number;
  $collapse?: boolean;
  $width?: number | string;
  $xs?: number;
  $sm?: number;
  $md?: number;
  $lg?: number;
  $xl?: number;
  $xsOffset?: number;
  $smOffset?: number;
  $mdOffset?: number;
  $lgOffset?: number;
  $xlOffset?: number;
  $sys: Record<string, any>;
  [key: string]: any;
}

function getPaddingPx(p: any): number {
  if (p.$collapse) return 0;
  const g =
    p.$gutter !== undefined ? p.$gutter : (p.theme && p.theme.gridGutter) || 16;
  return g / 2;
}

// Resolve a breakpoint to a CSS length: theme value first (numbers -> px),
// falling back to the molekule defaults.
function breakpointFor(theme: any, key: string): string {
  const v = theme && theme.breakpoints && theme.breakpoints[key];
  if (typeof v === 'number') return `${v}px`;
  if (typeof v === 'string') return v;
  return BREAKPOINTS[key];
}

// Build CSS string for all breakpoint rules
function buildBreakpointCSS(
  p: StyledColumnInternalProps & { theme?: any },
): string {
  const parts: string[] = [];

  // xs is mobile-first (no @media wrapper)
  if (p.$xs !== undefined) {
    parts.push(getColumnWidthCSS(p.$xs));
    if (p.$xsOffset !== undefined) parts.push(getOffsetCSS(p.$xsOffset));
  }

  const emit = (size: number | undefined, offset: number | undefined, key: string) => {
    if (size === undefined) return;
    const inner = [
      getColumnWidthCSS(size),
      offset !== undefined ? getOffsetCSS(offset) : '',
    ]
      .filter(Boolean)
      .join('\n');
    parts.push(`@media (min-width: ${breakpointFor(p.theme, key)}) { ${inner} }`);
  };
  emit(p.$sm, p.$smOffset, 'sm');
  emit(p.$md, p.$mdOffset, 'md');
  emit(p.$lg, p.$lgOffset, 'lg');
  emit(p.$xl, p.$xlOffset, 'xl');

  return parts.join('\n');
}

const StyledColumn = styled.div<StyledColumnInternalProps>`
  /* molekule Column renders on top of Flex (display: flex) */
  display: flex;
  box-sizing: border-box;
  flex: 1 0 auto;
  flex-direction: column;
  padding-left: ${(p: any) => getPaddingPx(p)}px;
  padding-right: ${(p: any) => getPaddingPx(p)}px;
  ${(p: any) =>
    p.$width !== undefined
      ? typeof p.$width === 'number'
        ? `width: ${p.$width}px;`
        : `width: ${p.$width};`
      : ''}
  ${(p: any) => buildBreakpointCSS(p)}
  ${(p: any) => system(p.$sys, p.theme)}
`;

const Column: React.FC<ColumnProps> = ({
  gutter,
  collapse,
  vertical,
  width,
  xs,
  sm,
  md,
  lg,
  xl,
  xsOffset,
  smOffset,
  mdOffset,
  lgOffset,
  xlOffset,
  children,
  ...props
}) => {
  const { sys, rest } = extractSysProps(props as Record<string, any>);

  return (
    <StyledColumn
      $gutter={gutter}
      $collapse={collapse}
      $width={width}
      $xs={xs}
      $sm={sm}
      $md={md}
      $lg={lg}
      $xl={xl}
      $xsOffset={xsOffset}
      $smOffset={smOffset}
      $mdOffset={mdOffset}
      $lgOffset={lgOffset}
      $xlOffset={xlOffset}
      $sys={sys}
      {...rest}
    >
      {children}
    </StyledColumn>
  );
};

Column.displayName = 'Column';

export default Column;
